import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useClinicConfigContext } from '@/contexts/ClinicConfigContext';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Footer } from '@/components/Footer';

export function RequestSubmittedPage() {
  const { clinicSlug } = useParams<{ clinicSlug: string }>();
  const navigate = useNavigate();
  const { clinic, clinicLoading, clinicError } = useClinicConfigContext();

  // Word-level animation variants
  const wordContainer = {
    hidden: { opacity: 1 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.15,
      },
    },
  };

  const wordItem = {
    hidden: { opacity: 0, y: 12 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] },
    },
  };

  if (clinicLoading) {
    return (
      <div className='min-h-screen relative'>
        <LoadingSpinner fullScreen size='lg' text='Loading...' />
      </div>
    );
  }

  if (clinicError || !clinic) {
    return (
      <div className='min-h-screen relative flex items-center justify-center'>
        <div className='text-center'>
          <h1 className='text-4xl font-bold text-gray-800 mb-4'>404</h1>
          <p className='text-xl text-gray-600 mb-8'>Clinic not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className='h-screen w-screen overflow-hidden relative flex flex-col'>
      <div className='flex-shrink-0 p-6'>
        <div className='flex items-center'>
          <div className='w-fit'>
            <img
              src={'/logo.png'}
              alt={`${clinic.name} logo`}
              className='w-full h-full object-contain'
            />
          </div>
        </div>
      </div>

      <div className='flex-1 flex flex-col justify-center px-6 py-8'>
        <div className='max-w-3xl mx-auto w-full text-left'>
          <motion.h2
            variants={wordContainer}
            initial='hidden'
            animate='visible'
            transition={{ duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }}
            className='text-2xl lg:text-6xl font-semibold'
            style={{ color: 'var(--clinic-text)' }}
          >
            {['Request', 'Submitted'].map((word, index) => (
              <motion.span
                className='inline-block mr-2'
                variants={wordItem}
                key={`h1-l1-${index}`}
              >
                {word}
              </motion.span>
            ))}
            <br />
            {['Expect', 'a', 'Call', 'From', 'Us', ':)'].map((word, index) => (
              <motion.span
                className='inline-block mr-2'
                variants={wordItem}
                key={`h1-l2-${index}`}
              >
                {word}
              </motion.span>
            ))}
          </motion.h2>

          <motion.p
            variants={wordContainer}
            initial='hidden'
            animate='visible'
            transition={{
              delay: 0.25,
              duration: 0.7,
              ease: [0.25, 0.46, 0.45, 0.94],
            }}
            className='mt-10 text-base lg:text-6xl font-semibold'
            style={{ color: 'var(--clinic-text)' }}
          >
            {['Meanwhile', 'check', 'our'].map((word, index) => (
              <motion.span
                className='inline-block mr-2'
                variants={wordItem}
                key={`p-l1-${index}`}
              >
                {word}
              </motion.span>
            ))}
            <br />{' '}
            {['AI', 'Insurance', 'Assistant'].map((word, index) => (
              <motion.span
                className='inline-block mr-2'
                variants={wordItem}
                key={`p-l2-${index}`}
                style={{ color: 'var(--clinic-text-secondary)' }}
              >
                {word}
              </motion.span>
            ))}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 0.5,
              duration: 0.6,
              ease: [0.25, 0.46, 0.45, 0.94],
            }}
            className='flex justify-start mt-10'
          >
            <button
              onClick={() => navigate(`/${clinicSlug}/insurance-details`)}
              className='text-white px-9 py-2.5 rounded-full font-semibold lg:text-lg text-base flex items-center justify-center transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl'
              style={{
                background: 'var(--clinic-secondary)',
              }}
            >
              Take me to the AI Tool
            </button>
          </motion.div>
        </div>
      </div>

      <Footer mobileColumnLayout={true} />
    </div>
  );
}

export default RequestSubmittedPage;
